import { useState, useRef, useCallback, useEffect } from 'react';
import { FaUser, FaIdCard, FaBriefcase, FaCamera } from 'react-icons/fa';
import Webcam from 'react-webcam';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { GoArrowRight } from "react-icons/go";
import { RiAlarmWarningFill } from "react-icons/ri";
import { Button, CircularProgress, Paper } from '@mui/material';
import { RiCheckDoubleFill } from "react-icons/ri";

import { IntegrationService } from '../../../Services/IntegrationService';
import { ResultFaceRecognition } from '../../../models/faceRecognitionType';
import { TypeLinkRegister } from '../../../enum/enums';
import { Employee } from '../../../models/employeeType';
import { EmployeeService } from '../../../Services/EmployeeService';
import { EventService } from '../../../Services/EventService';
import { useFormat } from '../../../hooks/useFormat';
import { Event } from '../../../models/eventType';
import { base64SizeInBytes } from '../../../Helpers/ImageHelper';

type Error = {
  name?: string,
  cpf?: string,
  setor?: string,
  function?: string,
  image?: string
}

type Props = {
  eventId: number;
  companyId: number;
  // Obrigatório no modo de atualização de foto (TypeLinkRegister.AtualizarFoto)
  employeeId?: number;
  typeLink: TypeLinkRegister;
  reloadTable: () => void;
  setFinish: (finish: boolean) => void;
}

const formSchema = z.object({
  name: z.string().min(1, 'O Nome é obrigaório'),
  cpf: z.string().min(1, 'O CPF é obrigaório')
    .transform(cpf => cpf.replaceAll('.', '').replaceAll('-', '')),
  setor: z.string().min(1, 'O Setor é obrigaório'),
  function: z.string(),
  image: z.string()
});

type FormSchemaType = z.infer<typeof formSchema>;

type PageState = 'carregando' | 'naoEncontrado' | 'indisponivel' | 'erro' | 'pronto';

const LINK_INDISPONIVEL = 'Este link não está disponível. Fale com o credenciamento.';

const FormStepper = (props: Props) => {
  var service = new IntegrationService();
  var employeeService = new EmployeeService();
  var eventService = new EventService();

  const [errors, setErrors] = useState<Error>({});
  const { formatMaskRgOrCpf } = useFormat();
  const [step, setStep] = useState(1);
  const webcamRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [faceDetect, setFaceDetect] = useState<ResultFaceRecognition>({} as ResultFaceRecognition);
  const [loading, setLoading] = useState(false);
  const [showFaceValidate, setShowFaceValidate] = useState(false);
  const [_event, setEvent] = useState<Event>();
  const isUpdatePhoto = props.typeLink === TypeLinkRegister.AtualizarFoto;
  const [pageState, setPageState] = useState<PageState>(isUpdatePhoto ? 'carregando' : 'pronto');
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    getValues,
    reset
  } = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      cpf: '',
      setor: '',
      function: '',
      image: ''
    }
  });



  const captureImage = useCallback(() => {
    if (webcamRef.current == null) return;
    //@ts-ignore
    const video: HTMLVideoElement = webcamRef.current.video;
    const scale = Math.min(1, 600 / video.videoWidth, 800 / video.videoHeight);
    //@ts-ignore
    const imageSrc = webcamRef.current.getScreenshot(video?.videoWidth > 0 ? {
      width: Math.round(video.videoWidth * scale),
      height: Math.round(video.videoHeight * scale)
    } : undefined);
    handlePhotoValidate(imageSrc);
  }, [webcamRef, setImgSrc]);

  const handlePhotoValidate = async (photoEmployee: any) => {
    setShowFaceValidate(false)
    setFaceDetect({} as ResultFaceRecognition);

    setLoading(true);

    setImgSrc(photoEmployee);
    var faceApiResponse = await service.handlePhotoValidateAsync(photoEmployee);
    setFaceDetect(faceApiResponse);

    setLoading(false);
    setShowFaceValidate(true);
  }

  const validateStep = (currentStep: any, hasEmployeePhoto: boolean) => {
    const newErrors: Error = {};
    if (currentStep === 1) {
      if (getValues('name') === '') newErrors.name = 'Campo e obrigatório';
      if (getValues('cpf') === '') newErrors.cpf = 'Campo e obrigatório';
      if (getValues('setor') === '') newErrors.setor = 'Campo e obrigatório';


    } else if (currentStep === 2 && hasEmployeePhoto) {
      if (!imgSrc) newErrors.image = 'A Foto e obrigatória';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (_event == null) return;

    const photoRequired = _event.hasEmployeePhoto || isUpdatePhoto;

    if (step == 1) {
      if (!photoRequired) {
        setStep(3)
        return;
      }
    }

    if (validateStep(step, photoRequired)) {
      setStep(step + 1);
    }
  };

  const handlePrev = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if ((_event?.hasEmployeePhoto || isUpdatePhoto) && !imgSrc) {
      setErrors({ image: 'A foto é obrigatória' });
      setStep(2);
      return;
    }

    const photoBase64 = (imgSrc as string | null)?.split(',')[1];
    if (photoBase64 && base64SizeInBytes(photoBase64) > 2 * 1024 * 1024) {
      setErrors({ image: 'A foto deve ter no máximo 2 MB. Tire uma nova foto.' });
      setStep(2);
      return;
    }

    if (isUpdatePhoto) {
      await handleUpdatePhoto(photoBase64!);
      return;
    }

    let employee: Employee = {
      employeeId: 0,
      eventId: props.eventId,
      companyId: props.companyId,
      fullName: getValues('name'),
      cpf: getValues('cpf'),
      staff: getValues('setor'),
      function: getValues('function'),
      photo: photoBase64 ?? '',
      photoUri: ''
    };

    var response = await employeeService.addEmployeeFromLinkRegister(employee);
    if (response.isSuccess) {
      props.setFinish(true);
    }
  };

  const handleUpdatePhoto = async (photo: string) => {
    setSubmitting(true);
    const response = await employeeService.updatePhotoFromLinkAsync({
      employeeId: props.employeeId!,
      eventId: props.eventId,
      companyId: props.companyId,
      photo: photo
    });
    setSubmitting(false);

    if (response.isSuccess) {
      props.setFinish(true);
      return;
    }

    if (response.errorList[0]?.errorMessage === LINK_INDISPONIVEL) {
      setPageState('indisponivel');
      return;
    }

    // Permite tirar outra foto sem reabrir o link
    setImgSrc(null);
    setFaceDetect({} as ResultFaceRecognition);
    setShowFaceValidate(false);
    setStep(2);
  };

  const loadEmployee = async () => {
    setPageState('carregando');
    const response = await employeeService.getEmployeeToRegisterAsync(props.employeeId!, props.eventId, props.companyId);
    const data = response.data;

    if (response.isSuccess && data?.enableRegister) {
      reset({
        name: data.fullName ?? '',
        cpf: data.cpf ?? '',
        setor: data.staff ?? '',
        function: data.function ?? '',
        image: ''
      });
      setPageState('pronto');
    } else if (response.isSuccess) {
      setPageState('indisponivel');
    } else if (response.message === 'network') {
      setPageState('erro');
    } else {
      setPageState('naoEncontrado');
    }
  };

  useEffect(() => {
    renderPhotoValidate();
  }, [faceDetect]);

  useEffect(() => {
    eventService.getEvent(props.eventId)
      .then((response) => {
        setEvent(response);
      });

    if (isUpdatePhoto)
      loadEmployee();
  }, []);

  const renderPhotoValidate: any = () => {
    if (!showFaceValidate)
      return;

    if (faceDetect.isValid) {
      return (
        <Paper elevation={10} className="bg-green-300 border-green-500 px-4 py-2 text-center text-2xl flex justify-center items-center">
          <div className="row">
            <span className='flex text-green-600'>
              {faceDetect.message}
            </span>
          </div>
        </Paper>
      )
    } else {
      return (
        <>
          {imgSrc && (
            <Paper elevation={10} className="px-4 py-4 text-center flex justify-center items-center">
              <div className="row">
                <p className='text-red-500 text-xl font-mono flex-col'>
                  <span className='mx-2 flex justify-center'>
                    Foto Reprovada <RiAlarmWarningFill className='text-2xl text-red-500' />
                  </span>

                  <span>
                    {faceDetect.message}.
                  </span>
                </p>
              </div>
            </Paper>
          )}
        </>

      )
    }
  }

  const lockedClass = isUpdatePhoto ? 'bg-gray-100 cursor-not-allowed' : '';

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-mono text-center">
              {isUpdatePhoto ? 'Confira os Seus Dados' : 'Insira os Dados Cadastrais'}
            </h2>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nome Completo</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="name"
                  className={`block w-full pl-10 py-3 border sm:text-sm rounded-md ${lockedClass}`}
                  readOnly={isUpdatePhoto}
                  {...register('name')}
                />
              </div>
              {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label htmlFor="cpfRg" className="block text-sm font-medium text-gray-700">CPF/RG</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaIdCard className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="cpfRg"
                  className={`block w-full pl-10 py-3 sm:text-sm border rounded-md ${lockedClass}`}
                  placeholder="000.000.000-00"
                  readOnly={isUpdatePhoto}
                  {...register('cpf')}
                />
              </div>
              {errors.cpf && <p className="mt-2 text-sm text-red-600">{errors.cpf}</p>}
            </div>
            <div>
              <label htmlFor="setor" className="block text-sm font-medium text-gray-700">Setor</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaBriefcase className="h-5 w-5 text-gray-400" />
                </div>
                <input {...register('setor')}
                  readOnly={isUpdatePhoto}
                  className={`block w-full pl-10 py-3 sm:text-sm border rounded-md ${lockedClass}`}
                />
              </div>
              {errors.setor && <p className="mt-2 text-sm text-red-600">{errors.setor}</p>}
            </div>
            <div>
              <label htmlFor="function" className="block text-sm font-medium text-gray-700">Função</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaBriefcase className="h-5 w-5 text-gray-400" />
                </div>
                <input {...register('function')}
                  readOnly={isUpdatePhoto}
                  className={`block w-full pl-10 py-3 sm:text-sm border rounded-md ${lockedClass}`}
                />
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-mono mb-2 text-center">Precisamos Agora Tirar uma Foto</h2>
            <div className="max-w-md mx-auto">
              <p className="text-sm text-gray-500 mb-4 text-center">Por favor, habilite a web cam para tirar a foto.</p>
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.8}
                className="w-full rounded-lg shadow-lg"
              />
              <button
                type="button"
                onClick={captureImage}
                style={{ backgroundColor: "rgb(0, 57, 88)" }}
                className="mt-4 w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {loading ? (
                  <CircularProgress />
                ) : (
                  <span className='flex justify-center items-center gap-2'>Tirar Foto <FaCamera className="mr-2 h-5 w-5" /></span>
                )}

              </button>
              <div className='mt-2'>{renderPhotoValidate()}</div>
              {errors.image && <p className="mt-2 text-sm text-red-600">{errors.image}</p>}
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-mono mb-4 text-center">Valide os Seus Dados</h2>
            {_event && (_event.hasEmployeePhoto || isUpdatePhoto) && (
              <div className="sm:col-span-2 flex flex-col items-center">
                <dt className="text-sm font-medium text-gray-500">Foto</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <img src={imgSrc ?? ""} alt="Captured" className="w-48 h-48 rounded-lg shadow-lg" style={{ color: 'black', backgroundClip: 'black', fill: 'black' }} />
                </dd>
              </div>
            )}
            <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
              <div className="sm:col-span-1 sm:text-sm md:text-2xl">
                <dt className="font-medium text-gray-500">Nome:</dt>
                <dd className="mt-1 text-gray-900">{getValues('name')}</dd>
              </div>
              <div className="sm:col-span-1 sm:text-sm md:text-2xl">
                <dt className="font-medium text-gray-500">CPF/RG:</dt>
                <dd className="mt-1 text-gray-900">{formatMaskRgOrCpf(getValues('cpf'))}</dd>
              </div>
              <div className="sm:col-span-1 sm:text-sm md:text-2xl">
                <dt className="font-medium text-gray-500">Setor:</dt>
                <dd className="mt-1 text-gray-900">{getValues('setor')}</dd>
              </div>
              <div className="sm:col-span-1 sm:text-sm md:text-2xl">
                <dt className="font-medium text-gray-500">Função:</dt>
                <dd className="mt-1 text-gray-900">{getValues('function') ?? '-'}</dd>
              </div>
            </dl>
          </div>
        );
      default:
        return null;
    }
  };

  if (isUpdatePhoto && pageState !== 'pronto') {
    return (
      <div className="py-8 lg:px-8">
        <Paper elevation={10} className='rounded-xl px-8 py-10 w-100 flex flex-col justify-center items-center gap-4 text-center text-2xl font-mono'>
          {pageState === 'carregando' && (
            <>
              <CircularProgress />
              <span>Carregando seus dados...</span>
            </>
          )}
          {pageState === 'indisponivel' && (
            <>
              <RiAlarmWarningFill className='text-5xl text-red-500' />
              <span>{LINK_INDISPONIVEL}</span>
            </>
          )}
          {pageState === 'naoEncontrado' && (
            <span>Funcionário não encontrado</span>
          )}
          {pageState === 'erro' && (
            <>
              <span>Não foi possível carregar os dados. Tente novamente.</span>
              <Button variant="contained" onClick={loadEmployee} style={{ backgroundColor: "rgb(0, 57, 88)" }}>
                Tentar novamente
              </Button>
            </>
          )}
        </Paper>
      </div>
    );
  }

  return (
    <div className="py-8 lg:px-8">
      <Paper elevation={10} className='rounded-xl px-8 w-100'>
        <div className="mb-8">
          <div className="relative pt-1">
            <div className="flex mb-2 items-end justify-center">
              <span className='text-2xl font-mono my-2'>
                Passo {step} de 3 <br />
              </span>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded" style={{ backgroundColor: "#0039583d" }}>
              <div
                style={{ width: `${(step / 3) * 100}%`, backgroundColor: "rgb(0, 57, 88)" }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center"
              ></div>
            </div>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-8 w-full border-0 p-0 md:p-8 mb-8">
          {renderStep()}

          {step == 1 && (
            <div className="flex justify-content-end">
              <button
                type="button"
                onClick={handleNext}
                style={{ backgroundColor: "rgb(0, 57, 88)" }}
                className="mb-4 inline-flex items-end px-4 py-3 border border-transparent text-xl font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Próximo <GoArrowRight className='text-2xl ml-4' />
              </button>
            </div>
          )}

          {step == 2 && (
            <div className="flex justify-between">
              <button
                type="button"
                onClick={handlePrev}
                className="mb-4 inline-flex items-center px-4 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handleNext}
                style={{ backgroundColor: "rgb(0, 57, 88)" }}
                disabled={!faceDetect.isValid}
                className="mb-4 inline-flex items-end px-4 py-3 border border-transparent text-xl font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Próximo <GoArrowRight className='text-2xl ml-4' />
              </button>
            </div>
          )}

          {step == 3 && (
            <>
              <div className="flex justify-between">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  fullWidth
                  className="mb-4 inline-flex items-end px-4 py-3 border border-transparent text-xl font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  style={{ backgroundColor: "rgb(0, 57, 88)" }}
                >
                  {submitting ? (
                    <CircularProgress size={28} style={{ color: 'white' }} />
                  ) : (
                    <>{isUpdatePhoto ? 'Atualizar Foto' : 'Finalizar Cadastro'} <RiCheckDoubleFill className='text-2xl ml-4' /></>
                  )}
                </Button>

              </div>
              <div className='text-xl'>
                Precisa corrigir alguma informação ? e so clicar no passo que deseja voltar
                <button style={{ color: "rgb(0, 57, 88)" }} onClick={() => setStep(1)} className="underline mx-2"> Dados Cadastrais </button>
                {_event && (_event.hasEmployeePhoto || isUpdatePhoto) && (
                  <>
                    ou <button style={{ color: "rgb(0, 57, 88)" }} onClick={() => setStep(2)} className="underline mx-2"> Tire uma Foto </button>
                  </>
                )}
              </div>
            </>
          )}
        </form>
      </Paper>
    </div >
  );
};

export default FormStepper;